/**
 * The transcript (unit 26B; v1's `ChatHistory`). Markdown through `<Rich>`.
 *
 * Full, it snaps a new message of the person's to the top and then leaves the
 * scroll alone — they own it. Compact, under a widget, it is only what came
 * after the person's last message, with no cap of its own: the widget gives
 * up its room down to a bar and two rows, and the replies scroll past that
 * (v2.01 — capped at 40vh, the newest replies took the room and were cut). A
 * new message of the person's folds them away again. A settled reply from the agent carries a feedback flag.
 * A write the agent asks for is a line too (`Request.tsx`), and a job a
 * person started (`JobBox.tsx`).
 *
 * After `/history` the thread holds the earlier messages (`history.ts`): the
 * newest page opens at its end, a scroll near the top reads the page before
 * and keeps the place, and a line says where each `/clear` was. A reply's
 * "Actions" shows the tool calls and the requests of its run.
 */
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";

import { api, type Schemas } from "../api/client";
import { ok } from "../api/query";
import { Rich } from "../ui/Rich";
import styles from "./ChatHistory.module.css";
import { momentText, parseStamp } from "../ui/dates";
import { afterLastMine, clearedAfter, lastMine, type Line } from "./fold";
import { loadOlder } from "./history";
import { JobBox } from "./JobBox";
import { Request, stateWords } from "./Request";
import { useChat } from "./store";
import { Thinking } from "./Thinking";

/** How near the top a scroll asks for the page before. */
const NEAR_TOP = 240;

export function ChatHistory({ compact = false }: { compact?: boolean }) {
  const lines = useChat((s) => s.lines);
  const running = useChat((s) => s.running);
  const earlier = useChat((s) => s.earlier);
  const box = useRef<HTMLDivElement>(null);
  const seen = useRef({ count: lines.length, first: lines[0]?.key, height: 0 });

  useLayoutEffect(() => {
    const node = box.current;
    const before = seen.current;
    seen.current = { count: lines.length, first: lines[0]?.key, height: node?.scrollHeight ?? 0 };
    if (!node || lines.length <= before.count) return;
    if (lines[0]?.key !== before.first) {
      // The earlier thread: its newest page opens at its end; an older page
      // goes above, and what the person was reading stays where it was.
      const shown = before.first !== undefined && lines.some((l) => l.key === before.first);
      node.scrollTop = shown
        ? node.scrollTop + (node.scrollHeight - before.height)
        : node.scrollHeight;
      return;
    }
    if (compact) {
      node.scrollTop = node.scrollHeight;
      return;
    }
    const mine = [...lines.slice(before.count)].reverse().find((line) => line.role === "user");
    const target = mine && node.querySelector<HTMLElement>(`[data-key="${mine.key}"]`);
    if (target)
      node.scrollTop += target.getBoundingClientRect().top - node.getBoundingClientRect().top;
  }, [lines, compact]);

  // Compact: the person's last message and what came after it. The message is
  // there to scroll up to (#60); nothing shows until something came after it.
  const start = compact ? lastMine(lines) : 0;
  if (compact && afterLastMine(lines) >= lines.length && !running) return null;
  return (
    <div
      className={compact ? `${styles.history} ${styles.compact}` : styles.history}
      ref={box}
      onScroll={(event) => {
        if (earlier?.next != null && event.currentTarget.scrollTop < NEAR_TOP) void loadOlder();
      }}
    >
      <div className={compact ? `${styles.inner} ${styles.compactInner}` : styles.inner}>
        {earlier && (
          <div className={styles.edge} role="status">
            {earlier.next === null
              ? "The start of the conversation"
              : earlier.loading
                ? "Reading earlier messages…"
                : "Scroll up for earlier messages"}
          </div>
        )}
        {lines.slice(start).map((line, index) => (
          <Fragment key={line.key}>
            <Message line={line} />
            <Cleared at={clearedAfter(lines, start + index)} />
          </Fragment>
        ))}
        {running && <Thinking compact={compact} />}
      </div>
    </div>
  );
}

/** Where a `/clear` was, in the earlier thread. */
function Cleared({ at }: { at: string | null }) {
  if (at === null) return null;
  return (
    <div className={styles.cleared} role="separator">
      {at ? `Cleared ${when(at)}` : "Cleared"}
    </div>
  );
}

function Message({ line }: { line: Line }) {
  if (line.job) {
    return (
      <div data-key={line.key}>
        <JobBox job={line.job} />
      </div>
    );
  }
  if (line.request) {
    return (
      <div data-key={line.key}>
        <Request request={line.request} />
      </div>
    );
  }
  const classes = [
    styles.message,
    line.role === "user" ? styles.user : styles.assistant,
    line.error && styles.error,
    line.status === "failed" && styles.unsent,
    line.earlier && styles.earlier,
  ]
    .filter(Boolean)
    .join(" ");
  const settled = line.role === "assistant" && !line.notice && !line.error && !line.streaming;
  return (
    <div className={classes} data-key={line.key}>
      <Rich source={line.content} className={styles.content} />
      {line.status === "failed" && <span className={styles.failed}>not sent</span>}
      {settled && <Foot line={line} />}
    </div>
  );
}

/**
 * Under a settled reply: the flag, then "Actions", on one row in one size and
 * colour; what either opens goes under the row (v2.01 — the flag was a line of
 * its own, and "Actions" was on the next, indented).
 */
function Foot({ line }: { line: Line }) {
  const [feedback, setFeedback] = useState<"closed" | "open" | "done">("closed");
  const [actions, setActions] = useState(false);
  return (
    <>
      <div className={styles.foot}>
        {feedback === "done" ? (
          <span className={styles.thanks}>noted</span>
        ) : (
          <button
            className={styles.flag}
            onClick={() => setFeedback(feedback === "open" ? "closed" : "open")}
            aria-label="Feedback"
            aria-expanded={feedback === "open"}
            title="Give feedback on this message"
          >
            <svg aria-hidden="true" width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 1v10M2 1h7l-2 3.5L9 8H2"
                stroke="currentColor"
                strokeWidth={1.3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {line.id !== null && (
          <button
            className={styles.actionsToggle}
            onClick={() => setActions(!actions)}
            aria-expanded={actions}
          >
            {actions ? "Hide actions" : "Actions"}
          </button>
        )}
      </div>
      {feedback === "open" && (
        <Feedback
          line={line}
          onClose={() => setFeedback("closed")}
          onSent={() => setFeedback("done")}
        />
      )}
      {actions && line.id !== null && <Actions id={line.id} />}
    </>
  );
}

/**
 * What the agent did to write a reply: its tool calls and its requests, read
 * when asked for. A reply with no run kept says so — that is not "nothing".
 */
function Actions({ id }: { id: number }) {
  const [found, setFound] = useState<Schemas["MessageActions"] | "failed" | null>(null);

  useEffect(() => {
    let live = true;
    void ok(api.GET("/api/messages/{message_id}/actions", { params: { path: { message_id: id } } }))
      .then((got) => live && setFound(got))
      .catch(() => live && setFound("failed"));
    return () => {
      live = false;
    };
  }, [id]);

  return (
    <div className={styles.actions}>
      {found === null && <span className={styles.none}>Reading…</span>}
      {found === "failed" && <span className={styles.none}>Couldn't read the actions.</span>}
      {found && found !== "failed" && !found.recorded && (
        <span className={styles.none}>No record of the actions for this reply.</span>
      )}
      {found && found !== "failed" && found.recorded && !found.steps.length && (
        <span className={styles.none}>
          {found.requests.length ? "No tool calls." : "No actions: the agent only replied."}
        </span>
      )}
      {found && found !== "failed" && (
        <>
          <ol className={styles.steps}>
            {found.steps.map((step, index) => (
              <li key={index} className={step.error ? styles.stepFailed : undefined}>
                <span className={styles.tool}>{step.tool}</span>
                {step.summary && <span className={styles.args}>{step.summary}</span>}
                {step.error && <span className={styles.stepError}>{step.error}</span>}
              </li>
            ))}
          </ol>
          {/* Said, not asked again: the request itself is a line of the thread. */}
          {found.requests.map((request) => (
            <div key={request.id} className={styles.asked}>
              {request.summary} — {stateWords(request.status, request.error ?? null)}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/** The open feedback field: Enter or Send sends it, Escape closes it. */
function Feedback({
  line,
  onClose,
  onSent,
}: {
  line: Line;
  onClose: () => void;
  onSent: () => void;
}) {
  const [state, setState] = useState<"open" | "sending">("open");
  const [text, setText] = useState("");
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => field.current?.focus(), []);

  const send = async () => {
    if (!text.trim()) return;
    setState("sending");
    try {
      await ok(
        api.POST("/api/agent-feedback", {
          body: { message_id: line.id, message_text: line.content, feedback: text.trim() },
        }),
      );
      onSent();
    } catch {
      setState("open");
    }
  };

  return (
    <div className={styles.feedback}>
      <input
        ref={field}
        placeholder="What was wrong or could be better?"
        value={text}
        aria-label="Feedback"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void send();
          if (e.key === "Escape") onClose();
        }}
      />
      <button className={styles.send} onClick={() => void send()} disabled={state === "sending"}>
        Send
      </button>
    </div>
  );
}

/** A stamp in the person's zone: "12 Oct 2026, 14:02". */
function when(stamp: string): string {
  const date = parseStamp(stamp);
  if (Number.isNaN(date.getTime())) return stamp;
  return momentText(date);
}
